/**
 * @file AppProviders.js
 * @description Higher-order component that wraps the application with global providers
 * for state management (Redux), persistence (Redux Persist), and localization (MUI).
 */
import React from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { store, persistor } from './redux/store/store';
import PropTypes from 'prop-types';
import 'driver.js/dist/driver.css';

/**
 * AppProviders Component
 *
 * Wraps the application hierarchy with necessary context providers.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be wrapped.
 * @returns {React.ReactElement} The wrapped application.
 */
const AppProviders = ({ children }) => {
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {children}
        </LocalizationProvider>
      </PersistGate>
    </Provider>
  );
};

AppProviders.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppProviders;
